import { apiClient } from "../API/axiosInstance";
import { logger } from "../utils/logger";
import {
  getDefaultWidgetSize,
  nextWidgetPosition,
} from "../components/Dashboard/dashboardLayout";

/**
 * Service class for Dashboard operations
 */
export class DashboardService {
  static async createDashboard(name, description, devices, organisation_id) {
    try {
      if (name === "") {
        return false;
      }

      let device_ids =
        devices && devices.length > 0 ? devices.map((device) => device.id) : [];

      let data = {
        name: name,
        description: description,
        devices: device_ids,
        organisation_id: organisation_id,
      };
      await apiClient.post("/dashboard/", data);
      return true;
    } catch (error) {
      logger.warn(error);
      return false;
    }
  }

  static async updateDashboard(layout, dashboard_id) {
    if (!dashboard_id) {
      return false;
    }

    if (!layout) {
      return false;
    }

    let data = {
      layout: layout,
    };

    try {
      let response = await apiClient.put(
        `/dashboard/${dashboard_id}/layout`,
        data
      );

      if (response && response.status >= 200 && response.status < 300) {
        return true;
      }

      return false;
    } catch (error) {
      logger.error("Dashboard update error:", error);
      return false;
    }
  }

  static async deleteDashboard(dashboardId) {
    if (!dashboardId) {
      logger.error("Dashboard delete error: No dashboard ID provided");
      return false;
    }

    try {
      const response = await apiClient.delete(`/dashboard/${dashboardId}`);
      return response && response.status === 200;
    } catch (error) {
      logger.error("Dashboard delete error:", error);
      return false;
    }
  }

  static async fetchDashboard(dashboardId) {
    if (!dashboardId) {
      return null;
    }

    try {
      const response = await apiClient.get(`/dashboard/${dashboardId}`);
      return response.data;
    } catch (error) {
      logger.error("Error fetching dashboard:", error);
      return null;
    }
  }

  static async fetchDashboards(orgID) {
    if (!orgID) {
      return [];
    }

    try {
      const response = await apiClient.get(`/dashboard/organisation/${orgID}`);
      return response.data || [];
    } catch (error) {
      logger.error("Error fetching dashboards:", error);
      return [];
    }
  }

  static async updateWidget(widgetId, dashboardId, data) {
    if (!widgetId || !dashboardId || !data) {
      return false;
    }

    const response = await apiClient.put(
      `/dashboard/${dashboardId}/widget/${widgetId}`,
      data
    );
    return response.status >= 200 && response.status < 300;
  }

  static async addWidget(widget, fields, dashboardId, dashboardWidgets) {
    try {
      if (!widget || !fields || !dashboardId) {
        return false;
      }

      const { w, h } = getDefaultWidgetSize(widget);
      const { x, y } = nextWidgetPosition(dashboardWidgets);
      const order = dashboardWidgets.length + 1;

      //we want to store fields object as json string with escaped quotes and commas, encode the object
      let safeFields = JSON.stringify(fields);

      let data = {
        dashboard_id: dashboardId,
        widget_id: widget,
        device_id: fields["Value_Field"] || fields["device"] || 0,
        field: safeFields,
        order: order,
        x: x,
        y: y,
        w: w,
        h: h,
      };

      const response = await apiClient.post(
        `/dashboard/${dashboardId}/widget`,
        data
      );
      return response.status >= 200 && response.status < 300;
    } catch (error) {
      logger.warn(error);
      return false;
    }
  }

  static async deleteWidget(widgetId, dashboardId) {
    if (!widgetId || !dashboardId) {
      return false;
    }
    const response = await apiClient.delete(
      `/dashboard/${dashboardId}/widget/${widgetId}`
    );

    return response.status >= 200 && response.status < 300;
  }

  static async editDashboard(dashboardId, data) {
    let result = await apiClient.put(`/dashboard/${dashboardId}`, data);

    if (result) {
      return true;
    } else {
      return false;
    }
  }

  static async fetchDashboardDevices(dashboardID) {
    if (!dashboardID) {
      return [];
    }

    const response = await apiClient.get(`/dashboard/${dashboardID}/devices`);
    return response.data;
  }

  static async shareDashboard(dashboardId, enabled) {
    const response = await apiClient.put(`/dashboard/${dashboardId}/share`, {
      enabled,
    });
    return response.data;
  }
}
